import { createContext, PropsWithChildren, useState } from 'react';

import { useSimulatedClusterDemoUi } from '@cast/infrastructure-demo';

import { UseNodes, useNodes } from './hooks/useNodes';

import { useCluster } from '@cast/shared-cluster-context-data-access';

const WORKLOADS_REFETCH_INTERVAL_MS = 30_000;

type NodeContextType = UseNodes & {
  selectedIds: string[];
  setSelectedIds: (value: string[] | ((value: string[]) => string[])) => void;
};

export const NodesContext = createContext<NodeContextType>({
  isLoading: true,
  isWorkloadsLoading: true,
  isPricingLoading: true,
  isProblematicNodesLoading: true,
  isFetching: false,
  isFetchingNextPage: false,
  hasNextPage: false,
  fetchNextPage: () => undefined,
  selectedIds: [],
  setSelectedIds: () => undefined,
  nodes: [],
  refetch: () => undefined,
  hasResultsWithError: false,
});

type Props = PropsWithChildren<{
  /** Force-fetch rebalance workloads (e.g. Inventory Nodes workload filter). */
  withRebalancingWorkloads?: boolean;
}>;

export const NodesProvider = ({
  children,
  withRebalancingWorkloads: withRebalancingWorkloadsOverride,
}: Props) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { isManaged, clusterOnboardingPhase2, isKentManaged, isKentEligible } =
    useCluster();
  const isSimulatedClusterDemoUi = useSimulatedClusterDemoUi();

  const withRebalancingWorkloads =
    withRebalancingWorkloadsOverride ??
    ((isManaged && !clusterOnboardingPhase2) ||
      isSimulatedClusterDemoUi ||
      isKentManaged ||
      isKentEligible);

  const useNodesResult = useNodes({
    withPricing: true,
    withIssues: true,
    workloadsRefetchInterval: WORKLOADS_REFETCH_INTERVAL_MS,
    withRebalancingWorkloads,
  });
  return (
    <NodesContext.Provider
      value={{
        ...useNodesResult,
        selectedIds,
        setSelectedIds,
      }}
    >
      {children}
    </NodesContext.Provider>
  );
};
